import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { NotificationsIcon } from "@/shared/ui/console-icons";

interface PageHeaderProps {
  title: ReactNode;
  /** Accessible route title retained when custom interactive title content is displayed. */
  accessibleTitle?: string;
  /** Page-specific filter controls registered via `useTopBarFilters`, rendered inline in the top bar. */
  filters?: ReactNode;
  /** Shows the notification bell button. Hidden on pages that manage notifications inline. */
  showNotificationBell?: boolean;
  /** Total active notification count shown on the bell badge. */
  notificationCount?: number;
  /** Whether the shared layout-level notification rail is currently expanded. */
  isNotificationsPanelOpen?: boolean;
  onToggleNotificationsPanel?: () => void;
}

/** Fixed page header height in pixels. Shared so dependent layout (e.g. right rails) can size against it. */
export const PAGE_HEADER_HEIGHT = 52;

function formatTime(): string {
  return new Date().toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function useLiveClock(): string {
  const [time, setTime] = useState(formatTime);
  useEffect(() => {
    const id = setInterval(() => setTime(formatTime()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

export function PageHeader({
  title,
  accessibleTitle,
  filters,
  showNotificationBell = false,
  notificationCount = 0,
  isNotificationsPanelOpen = false,
  onToggleNotificationsPanel,
}: PageHeaderProps) {
  const time = useLiveClock();

  return (
    <Box
      component="header"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 3,
        height: PAGE_HEADER_HEIGHT,
        minWidth: "max-content",
        flexShrink: 0,
        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
      })}
    >
      {typeof title === "string" ? (
        <Typography variant="h6" component="h1" sx={{ flexShrink: 0 }}>
          {title}
        </Typography>
      ) : (
        <>
          {accessibleTitle && (
            <Typography
              component="h1"
              sx={{
                position: "absolute",
                width: 1,
                height: 1,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
              }}
            >
              {accessibleTitle}
            </Typography>
          )}
          <Box component="nav" aria-label="Page views" sx={{ display: "flex", flexShrink: 0 }}>
            {title}
          </Box>
        </>
      )}
      {filters && (
        <Divider
          orientation="vertical"
          flexItem
          sx={(theme) => ({ my: 1.25, borderColor: alpha(theme.palette.common.white, 0.08) })}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          minWidth: "max-content",
        }}
      >
        {filters}
      </Box>
      {showNotificationBell && (
        <Tooltip title={isNotificationsPanelOpen ? "Hide notifications" : "Show notifications"}>
          <IconButton
            aria-label="Toggle notifications"
            aria-pressed={isNotificationsPanelOpen}
            onClick={onToggleNotificationsPanel}
            sx={{ color: "text.secondary", ml: "auto", mr: 1.5 }}
          >
            <Badge
              badgeContent={notificationCount}
              showZero={false}
              color="error"
              max={99}
              overlap="circular"
              sx={{ "& .MuiBadge-badge": { fontSize: "0.625rem", fontWeight: 700 } }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      <Box
        aria-live="off"
        sx={(theme) => ({
          ml: showNotificationBell ? 0 : "auto",
          px: 1.75,
          py: 0.625,
          borderRadius: "6px",
          backgroundColor: alpha(theme.palette.common.black, 0.25),
          border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        })}
      >
        <Typography
          sx={{
            fontFamily: '"Roboto Mono", "Courier New", monospace',
            fontVariantNumeric: "tabular-nums",
            fontSize: "0.9375rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: "text.primary",
          }}
        >
          {time}
        </Typography>
      </Box>
    </Box>
  );
}
